import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState, ErrorState, StatusPill } from '@/components/app';
import { paymentsApi, type Payment } from '@/api/payments';
import { getErrorMessage } from '@/lib/errors';
import { labelOf, PAYMENT_PROVIDER_LABELS, PAYMENT_STATUS_LABELS } from '@/lib/labels';
import { paymentStatusTone } from '@/lib/status';
import { formatTashkentShort } from '@/lib/datetime';

const LIMIT = 5;
const fmt = (n: number) => new Intl.NumberFormat('ru-RU').format(n);

/** Последние платежи (GET /payments?page=1&limit=5, сортировка по created_at DESC). */
export default function RecentPayments() {
  const [items, setItems] = useState<Payment[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchItems = useCallback(
    () =>
      paymentsApi
        .getAll({ page: 1, limit: LIMIT })
        .then((res) => setItems(res.items))
        .catch((err) => setError(getErrorMessage(err, 'Не удалось загрузить платежи'))),
    [],
  );

  useEffect(() => {
    void fetchItems();
  }, [fetchItems]);

  const load = () => {
    setError(null);
    setItems(null);
    void fetchItems();
  };

  return (
    <Card role="region" aria-label="Последние платежи" className="overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-[18px] py-4">
        <b className="font-display text-[17px] font-bold">Последние платежи</b>
        <Link to="/payments" className="rounded-md font-semibold text-accent-text outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring">
          Все платежи →
        </Link>
      </div>
      {error ? (
        <ErrorState message={error} onRetry={load} className="border-t border-border-soft py-8" />
      ) : items && items.length === 0 ? (
        <EmptyState title="Платежей пока нет" className="border-t border-border-soft py-8" />
      ) : (
        <Table className="min-w-[720px]">
          <TableHeader>
            <TableRow>
              <TableHead>Время</TableHead>
              <TableHead>Пользователь</TableHead>
              <TableHead>Тариф</TableHead>
              <TableHead>Провайдер</TableHead>
              <TableHead className="text-right">Сумма, сум</TableHead>
              <TableHead>Статус</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items === null
              ? Array.from({ length: LIMIT }, (_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 6 }, (__, j) => (
                      <TableCell key={j}>
                        <Skeleton className="h-4 w-full max-w-28" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : items.map((p) => {
                  const user = p.order?.user;
                  return (
                    <TableRow key={p.id}>
                      <TableCell className="whitespace-nowrap text-text-2 tabular-nums">{formatTashkentShort(p.created_at)}</TableCell>
                      <TableCell className="font-semibold">{user?.email || user?.username || '—'}</TableCell>
                      <TableCell>{p.order?.plan?.title ?? '—'}</TableCell>
                      <TableCell>{labelOf(PAYMENT_PROVIDER_LABELS, p.provider)}</TableCell>
                      <TableCell className="text-right tabular-nums">{fmt(p.amount)}</TableCell>
                      <TableCell>
                        <StatusPill tone={paymentStatusTone(p.status)}>{labelOf(PAYMENT_STATUS_LABELS, p.status)}</StatusPill>
                      </TableCell>
                    </TableRow>
                  );
                })}
          </TableBody>
        </Table>
      )}
    </Card>
  );
}
